<template>
    <div ref="rootEl" class="products-listing mb-el">
        <!-- Screen reader announcement -->
        <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            class="visually-hidden"
            :aria-label="resultAnnouncement"
        >
            {{ resultAnnouncement }}
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center py-5" role="status">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Lädt...</span>
            </div>
            <p class="mt-3">Produkte werden geladen...</p>
        </div>

        <!-- Error state -->
        <div
            v-else-if="error"
            class="alert alert-danger text-center"
            role="alert"
        >
            <p class="mb-3">{{ error }}</p>
            <button
                type="button"
                class="btn btn-primary"
                @click="fetchProducts"
            >
                Erneut versuchen
            </button>
        </div>

        <!-- Accordion with categories -->
        <template v-else>
            <div
                v-if="Object.keys(groupedProducts).length > 0"
                class="accordion__sections"
            >
                <div class="accordion accordion--styled" :id="accordionId">
                    <div
                        v-for="(
                            categoryData, categoryKey, index
                        ) in groupedProducts"
                        :key="categoryKey"
                        class="accordion-item"
                    >
                        <h3
                            class="accordion-header"
                            :id="`${accordionId}-heading-${index}`"
                        >
                            <button
                                class="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                :data-bs-target="`#${accordionId}-collapse-${index}`"
                                aria-expanded="false"
                                :aria-controls="`${accordionId}-collapse-${index}`"
                            >
                                {{ categoryData.name }}
                                <div class="accordion-button-icon">
                                    <ui-icon icon="chevron-24"></ui-icon>
                                </div>
                            </button>
                        </h3>
                        <div
                            :id="`${accordionId}-collapse-${index}`"
                            class="accordion-collapse collapse"
                            :aria-labelledby="`${accordionId}-heading-${index}`"
                            :data-bs-parent="`#${accordionId}`"
                        >
                            <div class="accordion-body vstack gap-3 pt-4">
                                <!-- Global price info -->
                                <div v-if="priceInfo" v-html="priceInfo"></div>

                                <!-- Legend with filter checkboxes -->
                                <div>
                                    <h4 class="h6 mb-2">Legende:</h4>
                                    <div class="d-flex flex-wrap gap-3">
                                        <div class="form-check">
                                            <input
                                                type="checkbox"
                                                v-model="
                                                    getCategoryFilter(
                                                        categoryKey
                                                    ).showStandard
                                                "
                                                class="form-check-input"
                                                :id="`filter-standard-${accordionId}-${index}`"
                                                :disabled="
                                                    !getCategoryFilter(
                                                        categoryKey
                                                    ).showCertified
                                                "
                                            />
                                            <label
                                                class="form-check-label"
                                                :for="`filter-standard-${accordionId}-${index}`"
                                            >
                                                <span
                                                    class="location-badge-outline"
                                                >
                                                    Herstellung ohne Überwachung
                                                </span>
                                            </label>
                                        </div>
                                        <div class="form-check">
                                            <input
                                                type="checkbox"
                                                v-model="
                                                    getCategoryFilter(
                                                        categoryKey
                                                    ).showCertified
                                                "
                                                class="form-check-input"
                                                :id="`filter-certified-${accordionId}-${index}`"
                                                :disabled="
                                                    !getCategoryFilter(
                                                        categoryKey
                                                    ).showStandard
                                                "
                                            />
                                            <label
                                                class="form-check-label"
                                                :for="`filter-certified-${accordionId}-${index}`"
                                            >
                                                <span class="location-badge">
                                                    <span>
                                                        Herstellung mit
                                                        Prüfzeugnis</span
                                                    >
                                                    <ui-icon
                                                        icon="check"
                                                        size="16"
                                                    ></ui-icon>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Products Table -->
                                <ProductsTable
                                    :products="categoryData.products"
                                    :show-standard="
                                        getCategoryFilter(categoryKey)
                                            .showStandard
                                    "
                                    :show-certified="
                                        getCategoryFilter(categoryKey)
                                            .showCertified
                                    "
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="alert alert-info text-center" role="status">
                Keine Produkte verfügbar.
            </div>
        </template>

        <!-- Location Modals -->
        <div
            v-for="location in uniqueLocations"
            :key="`modal-${location.id}`"
            class="modal fade"
            :id="`location-modal-${location.id}`"
            tabindex="-1"
            :aria-labelledby="`location-modal-${location.id}-label`"
            aria-hidden="true"
        >
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5
                            class="modal-title"
                            :id="`location-modal-${location.id}-label`"
                        >
                            {{ location.name }}
                        </h5>
                        <button
                            type="button"
                            class="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Schließen"
                        ></button>
                    </div>
                    <div class="modal-body vstack gap-4">
                        <!-- Company & Address -->
                        <div v-if="location.company || location.address">
                            <h6 class="fw-bold mb-2">{{ location.company }}</h6>
                            <p
                                v-if="location.address"
                                class="mb-0"
                                style="white-space: pre-line"
                            >
                                {{ location.address }}
                            </p>
                        </div>

                        <!-- Contact -->
                        <div v-if="location.contact">
                            <h6 class="fw-bold mb-2">Kontakt</h6>
                            <div v-html="location.contact"></div>
                        </div>

                        <!-- Opening Hours -->
                        <div v-if="location.openingHours">
                            <h6 class="fw-bold mb-2">Öffnungszeiten</h6>
                            <div v-html="location.openingHours"></div>
                        </div>

                        <!-- Description -->
                        <div v-if="location.description">
                            <div v-html="location.description"></div>
                        </div>

                        <!-- Empty state -->
                        <p
                            v-if="
                                !location.company &&
                                !location.address &&
                                !location.contact &&
                                !location.openingHours &&
                                !location.description
                            "
                            class="text-muted mb-0"
                        >
                            Keine weiteren Informationen verfügbar.
                        </p>
                    </div>
                    <div class="modal-footer">
                        <a
                            v-if="location.link"
                            :href="location.link"
                            target="_blank"
                            rel="noopener"
                            class="btn btn-primary"
                        >
                            Zur Standortseite
                        </a>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            data-bs-dismiss="modal"
                        >
                            Schließen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import ProductsTable from './ProductsTable.vue';

// Refs
const rootEl = ref(null);
const products = ref([]);
const loading = ref(false);
const error = ref(null);
const categoryFilters = ref({}); // Per-category filter state
const accordionId = `accordion-products-${Math.random().toString(36).substr(2, 9)}`;
const priceInfo = ref('');

// Methods
const getCategoryFilter = (categoryKey) => {
    if (!categoryFilters.value[categoryKey]) {
        categoryFilters.value[categoryKey] = {
            showStandard: true,
            showCertified: true,
        };
    }
    return categoryFilters.value[categoryKey];
};

const fetchProducts = async () => {
    loading.value = true;
    error.value = null;

    try {
        const response = await fetch('/api/products?site=prostein');
        const result = await response.json();

        if (result.success && result.data) {
            products.value = result.data;
        } else {
            error.value = 'Fehler beim Laden der Produkte.';
        }
    } catch (err) {
        error.value = 'Fehler beim Laden der Produkte.';
        console.error('Error fetching products:', err);
    } finally {
        loading.value = false;
    }
};

// Computed
const groupedProducts = computed(() => {
    const groups = {};

    products.value.forEach((product) => {
        const categoryKey = product.category?.key || 'uncategorized';
        const categoryName = product.category?.name || 'Sonstige';
        const categoryDescription = product.category?.description || '';
        const categorySortOrder = product.category?.sortOrder || 9999;

        if (!groups[categoryKey]) {
            groups[categoryKey] = {
                name: categoryName,
                description: categoryDescription,
                sortOrder: categorySortOrder,
                products: [],
            };
        }
        groups[categoryKey].products.push(product);
    });

    const sortedKeys = Object.keys(groups).sort((a, b) => {
        return groups[a].sortOrder - groups[b].sortOrder;
    });

    const sortedGroups = {};
    sortedKeys.forEach((key) => {
        sortedGroups[key] = groups[key];
    });

    return sortedGroups;
});

const uniqueLocations = computed(() => {
    const locationsMap = {};

    products.value.forEach((product) => {
        if (product.locations) {
            product.locations.forEach((loc) => {
                if (!locationsMap[loc.id]) {
                    locationsMap[loc.id] = {
                        id: loc.id,
                        code: loc.code,
                        name: loc.name,
                        company: loc.company,
                        address: loc.address,
                        contact: loc.contact,
                        openingHours: loc.openingHours,
                        description: loc.description,
                        link: loc.link,
                    };
                }
            });
        }
    });

    return Object.values(locationsMap);
});

const resultAnnouncement = computed(() => {
    const count = products.value.length;
    const categoryCount = Object.keys(groupedProducts.value).length;
    if (count === 0) {
        return 'Keine Produkte verfügbar.';
    }
    if (count === 1) {
        return `1 Produkt in ${categoryCount} Kategorie gefunden.`;
    }
    return `${count} Produkte in ${categoryCount} Kategorien gefunden.`;
});

// Lifecycle
onMounted(() => {
    if (rootEl.value) {
        const containerEl = rootEl.value.parentNode;
        if (containerEl && containerEl.hasAttribute('data-price-info')) {
            priceInfo.value = containerEl.getAttribute('data-price-info') || '';
        }
    }
    fetchProducts();
});
</script>
